import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Bot, Megaphone, Briefcase, ChevronDown } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeIn, Tag } from "@/components/Motion";

const TOPICS = [
    { icon: Megaphone, title: "Branding Basics", copy: "Naming, identity, voice, and how to be recognizable before you say a word." },
    { icon: Bot, title: "Creative Tools & AI", copy: "What today's tools can and can't do — and how to use them without losing your voice." },
    { icon: BookOpen, title: "Content Strategy", copy: "Planning content you can actually sustain, in a lane that fits you." },
    { icon: Briefcase, title: "Independent Business", copy: "Offers, pricing, and the unglamorous structure that makes creativity pay." },
];

const FAQ = [
    { q: "Do I have to like AI to be part of PAN?", a: "No. PAN welcomes different perspectives on AI. It's one tool in the kit — the human voice stays at the center. The AI Skeptic portal exists specifically so your concerns are heard." },
    { q: "Do I need experience or an audience to start?", a: "No. The Dreamer Program exists for people who have talent or ideas but don't know where to begin. Starting points are different for everyone — that's the point of the discovery form." },
    { q: "Does PAN guarantee income, fame, or a deal?", a: "No — and be suspicious of anyone who does. Outcomes depend on your effort, circumstances, and available opportunities. We help you put structure behind your vision." },
    { q: "What does it cost to get started?", a: "The discovery form is free. The PURP ALERT NETWORK Membership is $4.99/month USD — a recurring monthly subscription with terms shown clearly before anything ever charges." },
    { q: "Is PURP ALERT NETWORK a nonprofit?", a: "No. PURP ALERT NETWORK (PAN LLC) is a business being built today. PURP ALERT INC. is a separate, future nonprofit vision — not yet formed and not a 501(c)(3)." },
];

function Faq({ q, a, i }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-2xl bg-panel/75 border border-white/10 overflow-hidden" data-testid={`school-faq-${i}`}>
            <button className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`school-faq-toggle-${i}`}>
                <span className="font-display font-bold uppercase text-lg md:text-xl">{q}</span>
                <ChevronDown className={`w-5 h-5 text-purp-neon shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <div className="overflow-hidden">
                    <p className="px-6 pb-6 text-sm text-slate-400 leading-relaxed">{a}</p>
                </div>
            </div>
        </div>
    );
}

export default function PanSchool() {
    return (
        <div data-testid="pan-school-page">
            <PageHero
                eyebrow="PAN School"
                titleLines={["Learn the game.", "Then change it."]}
                copy="The educational side of PURP ALERT NETWORK — branding, creative tools, content strategy, and independent business development, explained for people just getting started."
            />

            <section className="py-16 md:py-24 border-t border-white/10" data-testid="school-topics">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {TOPICS.map((t, i) => (
                            <FadeIn key={t.title} delay={0.07 * i}>
                                <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-7 hover:border-purp/50 transition-colors duration-300" data-testid={`school-topic-${i}`}>
                                    <div className="flex items-center justify-between">
                                        <t.icon className="w-6 h-6 text-purp-neon" />
                                        <Tag tone="volt">In Development</Tag>
                                    </div>
                                    <h3 className="mt-8 font-display font-bold uppercase text-2xl leading-tight">{t.title}</h3>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t.copy}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-24 border-t border-white/10" data-testid="school-faq">
                <div className="max-w-3xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            Asked <span className="italic text-purp">&amp; answered.</span>
                        </h2>
                        <div className="mt-10 space-y-4">
                            {FAQ.map((f, i) => (
                                <Faq key={i} q={f.q} a={f.a} i={i} />
                            ))}
                        </div>
                        <Link to="/discover?path=unsure" data-testid="school-discover-cta" className="mt-12 inline-flex items-center gap-2 rounded-full bg-purp px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(168,85,247,0.7)]">
                            Find Your Starting Point <ArrowRight className="w-4 h-4" />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
}
